/**
 * LocationWeatherPanel — drops any recent weather activity for a
 * given location onto its `/l/<slug>` article page.
 *
 * Shows two stacks:
 *   1. AI-generated Weather Almanac Records (encyclopedic long-form)
 *   2. Citizen Weather Reports (near-real-time neighbourhood posts)
 *
 * Hidden entirely when both stacks are empty so the article page
 * doesn't render an empty section.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Cloud, Camera, AlertTriangle, ChevronRight } from "lucide-react";

const SEV_TONE = {
  Catastrophic: "bg-black text-white",
  Historic:     "bg-purple-100 text-purple-800",
  Severe:       "bg-red-100 text-red-800",
  Major:        "bg-orange-100 text-orange-800",
  Moderate:     "bg-yellow-100 text-yellow-800",
  Minor:        "bg-green-100 text-green-800",
  critical:     "bg-black text-white",
  severe:       "bg-red-100 text-red-800",
  moderate:     "bg-yellow-100 text-yellow-800",
  mild:         "bg-green-100 text-green-800",
};

export default function LocationWeatherPanel({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/location-weather/${encodeURIComponent(slug)}?days=30`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { if (slug) load(); }, [slug, load]);

  if (loading || !data) return null;
  const almanac = data.almanac_records || [];
  const reports = data.citizen_reports || [];
  if (almanac.length === 0 && reports.length === 0) return null;

  return (
    <section className="mt-10 border-t pt-8" data-testid="location-weather-panel">
      <div className="flex items-center gap-2 mb-4">
        <Cloud className="w-5 h-5 opacity-70" />
        <h2 className="font-display text-2xl">Weather activity</h2>
      </div>

      {almanac.length > 0 ? (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest opacity-60 mb-2">Almanac records</div>
          <div className="space-y-2">
            {almanac.slice(0, 6).map((r) => (
              <Link
                key={r.id}
                to={`/almanac/records/${r.slug || r.id}`}
                data-testid={`loc-almanac-${r.id}`}
                className="wofy-card !p-3 hover:shadow-md transition-shadow flex items-center gap-3"
              >
                <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded ${SEV_TONE[r.severity] || "bg-neutral-100"}`}>
                  {r.severity || "record"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-xs opacity-60">
                    {r.event_type} · {r.date || r.year}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 opacity-40" />
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {reports.length > 0 ? (
        <div>
          <div className="text-xs uppercase tracking-widest opacity-60 mb-2 flex items-center gap-1">
            <Camera className="w-3 h-3" /> Recent citizen reports
          </div>
          <div className="space-y-2">
            {reports.slice(0, 6).map((r) => (
              <Link
                key={r.id}
                to={`/weather/report/${r.id}`}
                data-testid={`loc-report-${r.id}`}
                className="wofy-card !p-3 hover:shadow-md transition-shadow flex items-center gap-3"
              >
                <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded ${SEV_TONE[r.severity] || "bg-neutral-100"}`}>
                  {r.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {r.type ? r.type.replace(/_/g, " ") : "Weather report"}
                    {r.description ? <span className="opacity-60"> — {String(r.description).replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").slice(0, 80)}</span> : null}
                  </div>
                  <div className="text-xs opacity-60">
                    by {r.author_name || r.author_handle || "Anonymous"}
                    {r.distance_km != null ? ` · ${r.distance_km} km away` : ""}
                    {" · "}
                    {new Date(r.created_at).toLocaleDateString()}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 opacity-40" />
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-4">
        <Link to="/weather?report=1" className="text-xs underline opacity-70 hover:opacity-100" data-testid="loc-add-report">
          + Report weather for this location
        </Link>
      </div>
    </section>
  );
}
